import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, ValidatorFn, Validators } from '@angular/forms';

import { EMAIL_LIST_SEPARATOR, emailListValidator, getEmailListEntries } from '../../../validators/email-list.validator';

/**
 * Renders a list of text boxes (one email each) for a value that is stored as a semicolon-separated string.
 * Use `EmailListInputComponent.toFormArray()` to build the `FormArray` and `EmailListInputComponent.toValue()` before submitting.
 */
@Component({
	selector: 'pk-broker-email-list-input',
	templateUrl: './email-list-input.component.html',
	standalone: false,
})
export class EmailListInputComponent implements OnInit {
	@Input() formArray: FormArray<FormControl<string>>;
	@Input() inputId: string;
	@Input() label: string;
	@Input() required = false;
	@Input() startValidation = false;
	@Input() placeholder = 'Ex. accounting@example.com';

	public static readonly maxEmailLength = 100;
	public readonly maxEmailLength = EmailListInputComponent.maxEmailLength;

	public static toFormArray(emails: string, validators: ValidatorFn[] = []): FormArray<FormControl<string>> {
		const entries = (emails ?? '')
			.split(EMAIL_LIST_SEPARATOR)
			.map(e => e.trim())
			.filter(Boolean);
		return new FormArray(
			(entries.length ? entries : ['']).map(e => EmailListInputComponent.getEmailControl(e)),
			validators,
		);
	}

	public static getValidators(options: { required?: boolean; maxLength?: number } = {}): ValidatorFn[] {
		return [emailListValidator(options)];
	}

	public static toValue(formArray: FormArray<FormControl<string>>): string {
		const emails = getEmailListEntries(formArray.value);
		return emails.length ? emails.join(EMAIL_LIST_SEPARATOR) : null;
	}

	private static getEmailControl(email = ''): FormControl<string> {
		return new FormControl(email, [Validators.email, Validators.maxLength(EmailListInputComponent.maxEmailLength)]);
	}

	public ngOnInit(): void {
		if (!this.formArray.length) {
			this.add();
		}
	}

	get groupErrors() {
		return this.startValidation ? this.formArray.errors : null;
	}

	public controlErrors(control: FormControl<string>) {
		return this.startValidation ? control.errors : null;
	}

	public add(): void {
		this.formArray.push(EmailListInputComponent.getEmailControl());
		this.formArray.markAsDirty();
	}

	public remove(index: number): void {
		this.formArray.removeAt(index);
		if (!this.formArray.length) {
			this.add();
		}
		this.formArray.markAsDirty();
	}
}
